import { Alert, Button, Card, CardContent, Spinner, Textarea, cn } from '@blink/ui';
import { attemptMissing } from '@blink/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Camera, Check, Circle, CircleX } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { CallButtons } from '@/components/call-bits';
import { ErrorAlert, Screen } from '@/components/common';
import { currentLocation } from '@/components/route-bits';
import { useI18n } from '@/i18n';
import { api, type AttemptView, type FailAnswer, type PhotoAnswer } from '@/lib/api';
import { newActionId } from '@/lib/ids';
import { sendOrKeep, useOutbox } from '@/lib/outbox';
import { prepareProofPhoto } from '@/lib/photo';
import { HelpTip } from '@/components/help-tip';

/**
 * Screens 5 and 8, Not delivered (plan 4.5): the reason, then the proof that reason needs —
 * calls from the in-app Call button far enough apart, a photo with GPS — shown as a checklist.
 * Save unlocks only when the proof is complete; the server checks it again.
 */
export function Fail() {
  const { t, f, formatTime } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  // kept: saved on the phone (weak signal), sent when the signal is back
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState<(FailAnswer & { kept?: boolean }) | null>(null);
  const view = useQuery({
    queryKey: ['courier', 'attempt', id],
    queryFn: () => api<AttemptView>(`/courier/parcels/${id}/attempt`),
    retry: false,
    // sending or saved: the parcel is leaving the route; a reload (the live update can come first) would answer "not found"
    enabled: !done && !sending,
  });
  const v = view.data;

  const [reasonId, setReasonId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  // one ID per attempt, made when the screen opens: a repeat send is counted once
  const [actionId] = useState(newActionId);
  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);
  // an attempt photo still on the phone (weak signal)
  const keptPhoto = useOutbox().filter((x) => x.shipmentId === id && x.state === 'WAITING' && x.type === 'PHOTO' && x.body.kind === 'ATTEMPT').at(-1);
  const photoNow = keptPhoto ? { hasGps: keptPhoto.body.latitude != null && keptPhoto.body.longitude != null } : (v?.photo ?? null);

  const photo = useMutation({
    mutationFn: async (file: File) => {
      const prepared = await prepareProofPhoto(file);
      setPreview(URL.createObjectURL(prepared.blob));
      return sendOrKeep<PhotoAnswer>({ id: newActionId(), type: 'PHOTO', shipmentId: id!, awb: v!.awb, body: { kind: 'ATTEMPT', ...prepared.fields }, blob: prepared.blob });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['courier', 'attempt', id] }),
  });
  const save = useMutation({
    mutationFn: async (): Promise<FailAnswer & { kept?: boolean }> => {
      const body = {
        clientActionId: actionId,
        failureReasonId: reasonId,
        note: note.trim() || null,
        location: await currentLocation(4000),
        // a photo still on the phone is named by its own ID until it is sent
        ...(keptPhoto ? { photoClientId: keptPhoto.id, photoField: 'photoEvidenceId' } : { photoEvidenceId: v?.photo?.id ?? null }),
      };
      const result = await sendOrKeep<FailAnswer>({ id: actionId, type: 'FAIL', shipmentId: id!, awb: v!.awb, body });
      return result.sent ? result.answer : { kept: true, attemptId: actionId, status: 'FAILED', callsCounted: v!.calls.valid, whatsappSent: false };
    },
    onMutate: () => setSending(true),
    onError: () => setSending(false),
    onSuccess: (answer) => {
      setDone(answer);
      void queryClient.invalidateQueries({ queryKey: ['courier'] });
    },
  });

  const title = <h1 className="truncate font-mono text-lg font-semibold">{v?.awb ?? '…'}</h1>;
  if (done) {
    return (
      <Screen title={title}>
        <Card data-testid="failed-saved">
          <CardContent className="space-y-3 pt-6 text-center">
            <CircleX className="mx-auto h-12 w-12 text-destructive" />
            <p className="text-lg font-semibold">{t.fail.saved}</p>
            {done.kept ? (
              <p className="rounded-lg bg-muted p-3 text-sm" data-testid="failed-kept">
                {t.outbox.keptAttempt}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">{done.whatsappSent ? t.fail.whatsappSent : t.fail.whatsappNotSent}</p>
            )}
            <p className="text-sm text-muted-foreground">{t.fail.keepParcel}</p>
            <Button className="h-12 w-full text-base" onClick={() => navigate('/', { replace: true })} data-testid="next-stop">
              {t.deliver.nextStop}
            </Button>
          </CardContent>
        </Card>
      </Screen>
    );
  }

  const reason = v?.reasons.find((r) => r.id === reasonId) ?? null;
  const missing = v ? attemptMissing({ reason, rules: v.rules, calledAt: v.calls.list.map((c) => c.at), photo: photoNow }) : ['REASON'];
  const needsCalls = !!reason?.requiresCallProof;
  const needsPhoto = !!reason?.requiresPhoto && !!v?.rules.gpsPhotoOnFailure;

  return (
    <Screen
      title={title}
      actions={
        <Button variant="ghost" size="icon" className="order-first text-white hover:bg-white/10 hover:text-white" aria-label={t.parcel.back} onClick={() => navigate(`/parcels/${id}`)} data-testid="back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
      }
    >
      {view.isLoading ? <Spinner /> : null}
      <ErrorAlert error={view.error} />
      {v ? (
        <>
          <section className="space-y-2">
            <h2 className="flex items-center gap-1 font-semibold">{t.fail.reasonTitle} <HelpTip id="courier.fail" className="text-muted-foreground" /></h2>
            <div className="grid gap-2" role="radiogroup">
              {v.reasons.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  role="radio"
                  aria-checked={reasonId === r.id}
                  onClick={() => setReasonId(r.id)}
                  className={cn('rounded-lg border px-4 py-3 text-start text-sm font-semibold', reasonId === r.id ? 'border-primary bg-primary text-primary-foreground' : 'bg-card')}
                  data-testid="reason"
                >
                  {r.name}
                </button>
              ))}
            </div>
          </section>

          {reason ? (
            <section className="space-y-3 rounded-xl border bg-card p-4" data-testid="proof-list">
              <h2 className="font-semibold">{t.fail.proofTitle}</h2>

              {needsCalls ? (
                <Item ok={!missing.includes('CALLS')} testId="proof-calls">
                  <p className="font-semibold">{f(t.fail.calls, { valid: Math.min(v.calls.valid, v.rules.callsRequired), required: v.rules.callsRequired, minutes: v.rules.minMinutesBetweenCalls })}</p>
                  {v.hasPhone ? (
                    <CallButtons shipmentId={v.id} calls={v.calls} hasAlt={v.hasAltPhone} />
                  ) : (
                    <Alert variant="warning">{t.fail.noPhone}</Alert>
                  )}
                </Item>
              ) : null}

              {needsPhoto ? (
                <Item ok={!missing.includes('PHOTO')} testId="proof-photo">
                  <p className="font-semibold">{t.fail.photo}</p>
                  <p className="text-xs text-muted-foreground">{t.fail.photoHint}</p>
                  <label className={cn('flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-md border text-base font-semibold', photo.isPending ? 'pointer-events-none opacity-50' : 'bg-background')}>
                    <Camera className="h-5 w-5" />
                    {photo.isPending ? t.deliver.photoSaving : photoNow ? t.deliver.retake : t.deliver.takePhoto}
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="sr-only"
                      disabled={photo.isPending}
                      data-testid="attempt-photo"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) photo.mutate(file);
                      }}
                    />
                  </label>
                  {preview ? <img src={preview} alt="" className="max-h-40 w-full rounded-lg object-cover" /> : null}
                  {keptPhoto ? (
                    <p className="text-xs text-warning-foreground" data-testid="attempt-photo-kept">
                      {photoNow?.hasGps ? t.outbox.attemptPhotoKept : t.fail.noGps}
                    </p>
                  ) : v.photo ? (
                    v.photo.hasGps ? (
                      <p className="text-xs text-success" data-testid="attempt-photo-saved">{f(t.fail.photoSaved, { time: formatTime(v.photo.takenAt) })}</p>
                    ) : (
                      <Alert variant="warning" data-testid="attempt-photo-no-gps">{t.fail.noGps}</Alert>
                    )
                  ) : null}
                  <ErrorAlert error={photo.error} />
                </Item>
              ) : null}

              <Item ok={true} testId="proof-whatsapp">
                <p className="text-sm">{!v.rules.whatsappOnFailure ? t.fail.whatsappOff : v.hasPhone ? t.fail.whatsappWillSend : t.fail.whatsappNoNumber}</p>
              </Item>

              <div className="space-y-1">
                <label htmlFor="note" className="text-sm font-semibold">{t.fail.note}</label>
                <Textarea id="note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={300} data-testid="fail-note" />
              </div>
            </section>
          ) : null}

          <div className="sticky bottom-0 -mx-4 space-y-2 border-t bg-background/95 p-4 backdrop-blur">
            {reason && missing.length ? (
              <p className="text-xs text-muted-foreground" data-testid="still-missing">
                {t.deliver.missing} {missing.map((m) => t.fail.missing[m as 'CALLS' | 'PHOTO']).join(' · ')}
              </p>
            ) : null}
            <ErrorAlert error={save.error} />
            <Button variant="destructive" className="h-14 w-full text-base" disabled={missing.length > 0 || save.isPending} onClick={() => save.mutate()} data-testid="save-fail">
              {save.isPending ? t.deliver.saving : t.fail.save}
            </Button>
          </div>
        </>
      ) : null}
    </Screen>
  );
}

/** One line of the proof-of-attempt checklist (screen 8): done or still needed. */
function Item({ ok, testId, children }: { ok: boolean; testId: string; children: ReactNode }) {
  return (
    <div className="flex gap-3" data-testid={testId} data-done={ok}>
      <span className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full', ok ? 'bg-success text-white' : 'bg-muted text-muted-foreground')}>
        {ok ? <Check className="h-4 w-4" /> : <Circle className="h-3 w-3" />}
      </span>
      <div className="min-w-0 flex-1 space-y-2">
        {children}
      </div>
    </div>
  );
}
